import React from 'react';
import { Link } from 'react-router-dom';
import { FiPhone, FiArrowRight } from 'react-icons/fi';
import LocationVehiclePage from './LocationVehiclePage';

const seoConfig = {
  city: 'Bridgeport',
  state: 'TX',
  slug: 'bridgeport-tx',
  county: 'Wise County',
  title: 'Used Cars Bridgeport TX - Trucks and Vehicles Near Lake Bridgeport',
  h1: 'Used Cars Near Bridgeport, TX',
  metaDescription: 'Used cars and trucks near Bridgeport TX. 6 minutes from Lake Bridgeport in Paradise. Trucks that tow, golf carts for the lake, European cars. Financing for all credit. (817) 382-2316.',
  ogTitle: 'Used Cars & Trucks Near Lake Bridgeport - Paradise-Performance',
  ogDescription: 'Bridgeport buyers skip the Fort Worth drive and save thousands. Trucks, golf carts, European cars — all 6 minutes east on 380.',
  ctaHeadline: 'Ready to Come See Us From Bridgeport?',
  ctaSubline: 'Six minutes on 380. No traffic. No dealership games. Just good vehicles at honest prices.',
  faqs: [
    {
      q: 'How far is Paradise-Performance from Bridgeport?',
      a: 'About 6 minutes. Take US-380 east out of Bridgeport, turn onto FM 1655 toward Paradise. No highway driving. No tolls. You\'ll pass the gas station and we\'re on the right.'
    },
    {
      q: 'Do you sell golf carts near Lake Bridgeport?',
      a: 'Yes. We carry both gas and electric golf carts. Lifted models for gravel roads, street-legal options for neighborhoods around the lake. We also service and repair golf carts on site — no shipping one to a Fort Worth shop.'
    },
    {
      q: 'What kind of trucks do you have for towing boats?',
      a: 'We keep trucks that can actually work. Half-tons and three-quarter-tons, mostly. If you\'re towing a bass boat off Bridgeport, a half-ton handles it fine. Bigger boat or a camper? We can talk three-quarter-ton options.'
    },
    {
      q: 'Can I finance a vehicle with bad credit?',
      a: 'Yes. We work with lenders who handle every credit situation — bad credit, no credit, first-time buyers. Most approvals come back same day. Apply online or walk in.'
    },
    {
      q: 'Do you accept trade-ins from Bridgeport?',
      a: 'Bring your vehicle in and we\'ll appraise it same day. Trade-in value gets applied directly to whatever you buy. We take all makes and models.'
    }
  ]
};

const BridgeportTX = () => (
  <LocationVehiclePage config={seoConfig}>

    {/* ─── Hero ─── */}
    <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 25% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 75% 50%, rgba(217,119,6,0.15) 0%, transparent 50%)'
        }} />
      </div>
      <div className="relative container mx-auto px-4">
        <div className="max-w-4xl">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Used Cars Near Bridgeport, TX
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-300 max-w-3xl">
            Six minutes from Bridgeport. Trucks that tow, golf carts for the lake and vehicles that don't come with a Fort Worth price tag.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/inventory"
              className="inline-flex items-center justify-center px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-lg transition-colors"
            >
              View Inventory <FiArrowRight className="ml-2" />
            </Link>
            <a
              href="tel:+18173822316"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white rounded-lg font-semibold text-lg hover:bg-white/20 transition-colors"
            >
              <FiPhone className="mr-2" /> (817) 382-2316
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Why Bridgeport Buyers Come to Us ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2">Bridgeport, TX</p>
          <h2 className="text-3xl font-bold mb-6">Why Bridgeport Folks Drive 6 Minutes to Buy From Us</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
            <p>If you live in Bridgeport, you know the options aren't great. You can drive an hour into Fort Worth and deal with traffic, high-pressure salesmen and prices that got inflated twice before you walked in the door. Or you can head east on 380 for six minutes.</p>
            <p>That's us. Paradise-Performance. We've been out here since before the roundabout went in on 380, selling vehicles to the same families who fish Lake Bridgeport on weekends and work in Decatur during the week. We're not some mega-lot. We're a shop with a lot next to it. We sell cars and fix them in the same place.</p>
            <p>Our overhead is low because we're on a county road in Paradise. Not an I-20 frontage pad paying $15,000 a month in rent. That difference goes straight to pricing. <Link to="/used-cars-decatur-tx" className="text-amber-600 hover:underline">Decatur buyers</Link> figured this out years ago. Bridgeport's catching on.</p>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Lake Bridgeport Section ─── */}
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2">Lake Life</p>
          <h2 className="text-3xl font-bold mb-6">Vehicles for Lake Bridgeport Property Owners</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>If you own lakefront on Bridgeport, you probably need two things — a truck that can tow your boat and a golf cart that doesn't die on gravel. We sell both.</p>
              <p>Our golf cart lineup includes gas and electric models. Lifted carts for the dirt roads around the lake. Street-legal options for the neighborhoods in Runaway Bay. Utility carts for maintaining acreage. Buy one from us and we service it right here. No boxing it up and shipping it to some dealer in Dallas.</p>
              <p>For tow rigs, we keep half-tons and three-quarter-tons that can actually pull weight. Not some base-model sedan lot. Real trucks for Bridgeport life.</p>
            </div>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>Here's the thing about the lake — humidity eats everything. You leave a golf cart outside for a season and the wiring corrodes. Your boat trailer rusts underneath. A truck sitting in wet grass for months grows mold in the carpet.</p>
              <p>We know this because we've fixed all of it. Our shop sees vehicles from around the lake every week. That gives us a different perspective than some DFW lot. We know what holds up out here and what doesn't. We stock accordingly.</p>
              <p>We also carry <Link to="/trailers" className="text-amber-600 hover:underline">utility trailers</Link> — popular with lake property owners hauling materials, equipment or extra gear between properties.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Neighborhoods We Serve ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2 text-center">Service Area</p>
          <h2 className="text-3xl font-bold text-center mb-10">Where Our Bridgeport Buyers Live</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Runaway Bay</h3>
              <p className="text-gray-600">Lakefront community north of the bridge. Lots of golf cart buyers and boat owners who need tow-capable trucks. We've sold dozens of carts to Runaway Bay residents.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">FM 920 Corridor</h3>
              <p className="text-gray-600">Ranch properties west of Bridgeport toward Chico. Acreage owners who need working trucks and utility vehicles. Big lots mean room for trailers too.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Downtown Bridgeport</h3>
              <p className="text-gray-600">Right near the old bridge and Main Street. Daily drivers, commuter cars for the ride into Decatur or DFW. Fuel-efficient vehicles that handle 380 every morning.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">North Shore</h3>
              <p className="text-gray-600">Properties along the north side of Lake Bridgeport. Weekend cabins, retirement homes and year-round residents. Golf carts and SUVs are the top sellers here.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">380 & 114 Junction</h3>
              <p className="text-gray-600">Growing area west of town. Newer construction, young families moving in. Need reliable family vehicles without metro pricing.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Chico & Alvord</h3>
              <p className="text-gray-600">Just north of Bridgeport. Small towns where people drive trucks and don't like driving to Fort Worth. We're closer than any other dealer with a full shop.</p>
            </div>
          </div>
          <p className="text-center text-gray-500 mt-8">
            Not listed here? If you're anywhere around Lake Bridgeport or <Link to="/used-cars-wise-county" className="text-amber-600 hover:underline">Wise County</Link>, we're your closest real dealership.
          </p>
        </div>
      </div>
    </section>

    {/* ─── What Makes Us Different ─── */}
    <section className="py-16 bg-gray-900 text-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <p className="text-amber-400 font-semibold mb-2 text-center">Why Choose Us</p>
          <h2 className="text-3xl font-bold text-center mb-10">Not Your Typical Used Car Lot</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="font-bold text-lg mb-2">We Fix What We Sell</h3>
              <p className="text-gray-400 text-sm">Full repair shop on site. European cars, diesel trucks, general maintenance. Buy here and we handle warranty work ourselves.</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="font-bold text-lg mb-2">Rural Prices</h3>
              <p className="text-gray-400 text-sm">Our lot sits on a county road. No strip-mall rent. No giant sales team. That savings goes directly into lower prices.</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="font-bold text-lg mb-2">Financing That Works</h3>
              <p className="text-gray-400 text-sm">Bad credit. No credit. Self-employed. Seasonal income. We work with lenders who understand how Wise County people earn a living.</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="font-bold text-lg mb-2">Real People</h3>
              <p className="text-gray-400 text-sm">No high-pressure sales floor. Walk the lot. Ask questions. Leave if you want. Come back when you're ready. We're not going anywhere.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ─── The Drive ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">The Drive From Bridgeport</h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-4">Head east on US-380 out of Bridgeport. About 6 minutes down the road, you'll hit FM 1655. Turn south. We're right there on Co Rd 3470 in Paradise. No highway driving. No tolls. No fighting your way through DFW traffic.</p>
          <p className="text-gray-700 text-lg leading-relaxed">Most Bridgeport folks tell us the drive felt shorter than going to Walmart in Decatur. That six minutes saves you thousands compared to anything on the I-20 corridor.</p>
        </div>
      </div>
    </section>

    {/* ─── Other Area Pages Cross-Link ─── */}
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">We Serve the Whole Region</h2>
          <p className="text-center text-gray-600 mb-6">Our Paradise lot sits at the center of it all. Whether you're in Wise County or Parker County, we're closer than you think.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/used-cars-decatur-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Decatur</h3>
              <p className="text-gray-500 text-sm">10 min on 81/287. County seat buyers save big.</p>
            </Link>
            <Link to="/used-cars-wise-county" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Wise County</h3>
              <p className="text-gray-500 text-sm">We're in the center of it. Every town in reach.</p>
            </Link>
            <Link to="/used-cars-weatherford-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Weatherford</h3>
              <p className="text-gray-500 text-sm">30 min drive beats I-20 markup every time.</p>
            </Link>
            <Link to="/used-cars-denton-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Denton</h3>
              <p className="text-gray-500 text-sm">Straight shot on 380. Students save thousands.</p>
            </Link>
          </div>
        </div>
      </div>
    </section>

  </LocationVehiclePage>
);

export default BridgeportTX;
